import { FacetsResult } from '~/lib/algolia/fetch-facets'
import { FACETS } from '~/lib/algolia/sync/set-settings'

import { stringIsNotEmpty } from '~/utils/check-empty-string'

import { AlgoliaInitialSearchResponse } from '~/data/serialize-algolia-initial-search'

export const SUB_CATEGORIES_SEPARATOR = ' > '

export function serializeAlgoliaResults(hits: any[]) {
  return hits?.map((hit) => {
    if (hit?.productCard) return hit

    const { shopify, storyblok, ...rest } = hit ?? {}

    return {
      ...rest,
      shopifyId: shopify?.shopifyId ?? null,
      ...storyblok,
      wishlistDetails: {
        inList: true,
        uuid: storyblok?.uuid ?? null,
        variantId: shopify?.defaultVariant?.id,
      },
      variants: shopify?.variants ?? null,
      productCard: {
        ...shopify,
        ...storyblok,
      },
    }
  })
}

export function processAlgoliaFilters(filters: string[]) {
  return (
    filters?.filter((string) => stringIsNotEmpty(string))?.join(' AND ') ?? ''
  )
}

/**
 * It takes a list of facets and returns a list of facets, but with the subcategories of each category
 * added to the category
 * @param {FacetsResult[]} facets - FacetsResult[]
 * @returns An array of facets, with the subcategories nested under the categories.
 */
function processCategoriesAndSubCategoriesFacets(facets: FacetsResult[]) {
  // Isolate categories facet
  const categories = facets?.find(
    (entry) => entry?.type === FACETS.CATEGORIES_V2,
  )
  // Isolate sub categories facet
  const subCategories = facets?.find(
    (entry) => entry?.type === FACETS.SUB_CATEGORIES,
  )

  return facets
    ?.filter((facet) => {
      // If there is no facet categories then we don't want to hide the sub categories
      if (!categories) return facet
      // If there is a categories facet then we want to hide to hide the sub categories
      // As they will be displayed under each relevant category
      return facet?.type !== FACETS.SUB_CATEGORIES
    })
    ?.map((facet) => {
      // If there is no categories facet
      if (!categories && facet?.type === FACETS.SUB_CATEGORIES) {
        return {
          ...facet,
          // Map over each sub categories facet value
          values: facet?.values?.map((entry) => {
            const hasParentFacet = entry?.label?.includes(
              SUB_CATEGORIES_SEPARATOR,
            )
            return {
              ...entry,
              // Remove parent category string (category > sub category)
              label: hasParentFacet
                ? entry?.label?.split(SUB_CATEGORIES_SEPARATOR)?.[1]
                : entry?.label,
            }
          }),
        }
      }

      // If there are categories and current facet is not category then it doesn't need to be processed
      if (facet?.type !== FACETS.CATEGORIES_V2) return facet

      return {
        // Spread categories facet
        ...facet,
        // Add a sub values object with relevant sub categories
        subValues: {
          type: FACETS.SUB_CATEGORIES,
          values: facet?.values?.reduce((acc, entry) => {
            // Isolate category name (eg: Bagues)
            const categoryName = entry.label
            // Isolate the category string to trim (eg: Bagues >)
            const str = `${categoryName} >`

            return {
              ...acc,
              // Create object {[catName]: [...subValues]}
              // eg: {Bagues: [{label: "Alliances", value: "Bagues > Alliances"}]}
              [categoryName]:
                subCategories?.values
                  // Filter to retrieve sub categories belonging to current category
                  ?.filter((item) => item?.label?.startsWith(str))
                  ?.map((item) => ({
                    ...item,
                    // Format facet label string
                    label: item?.label?.replace(str, ''),
                  })) ?? [],
            }
          }, {}),
        },
      }
    })
}

const STONES_FACET_VALUES_AMOUNT_TO_DISPLAY = 15

/**
 * It takes the initial data from Algolia and returns an array of facets
 * @param {AlgoliaInitialSearchResponse} data - AlgoliaInitialSearchResponse
 * @returns An array of objects with the following shape:
 * {
 *   type: string,
 *   values: [
 *     {
 *       label: string,
 *       value: string,
 *     }
 *   ]
 * }
 */
export function getDefaultFacetsFromAlgoliaProps(
  data: AlgoliaInitialSearchResponse,
  filters,
) {
  const facets = data?.initialData?.pages?.[0]?.facets ?? {}

  const processedFacets =
    Object.entries(facets)
      ?.filter(([key]) => {
        return !Object.keys(filters)?.includes(key)
      })
      ?.map(([key, value]) => {
        /**
         * For the stores facet values, WB team decided decided there were too many options listed
         * To fix this, we will only display the X stones with the most results
         */
        const values =
          // If current facet is stones
          key === FACETS.STONE
            ? Object.keys(value)
              // Sort stone values by results amount
              ?.sort(
                (currentKey, nextKey) => value[nextKey] - value[currentKey],
              )
              // Keep the first X facets
              ?.slice(0, STONES_FACET_VALUES_AMOUNT_TO_DISPLAY)
              // Then sort them back to alphabetical order
              ?.sort()
            : // For any other facet keep the default behaviour
            Object.keys(value)

        return {
          type: key,
          values:
            values
              ?.map((val) => ({ label: val, value: val }))
              ?.filter((item) => item) ?? [],
        }
      }) ?? []

  return processCategoriesAndSubCategoriesFacets(processedFacets)
}
